import { useState, useContext } from 'react'
import { Card, Table, Button, Alert } from 'react-bootstrap'
import { toast } from 'react-toastify'
import UserContext from 'context/user-context'
import TableLoading from 'shared/tables/table-loading'
import TableError from 'shared/tables/table-error'
import UserStatusBadge from 'shared/badges/user-status-badge'
import AppBadge from 'shared/badges/app-badge'
import { errorMessage } from 'admin/shared/errors'
import {
  useMunicipalityAdminUsers,
  useBlockMunicipalityAdminUser,
  useUnblockMunicipalityAdminUser,
  useSendMunicipalityAdminPasswordReset
} from 'api/hooks/use-municipality-admin-users'
import UserFormModal from './UserFormModal'
import UserReportsModal from './UserReportsModal'
import DatumTagsCard from './DatumTagsCard'

const COLUMNS = 6

const DATUM_ROLE_LABELS = { datum_user: 'User', datum_admin: 'Admin' }

// Derive display status from the locally mirrored flags — the list endpoint
// doesn't do a live Auth0 lookup per row.
const statusFor = (user) => {
  if (!user.has_auth0_account) return 'deleted'
  return user.blocked ? 'blocked' : 'active'
}

const UserSettings = () => {
  const currentUser = useContext(UserContext)
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [reportsUser, setReportsUser] = useState(null)

  const { data: users, isLoading, isError } = useMunicipalityAdminUsers()

  const onActionError = (fallback) => (err) => toast.error(errorMessage(err, fallback))
  const blockUser = useBlockMunicipalityAdminUser({
    options: { onSuccess: () => toast.success('User blocked'), onError: onActionError('Failed to block user') }
  })
  const unblockUser = useUnblockMunicipalityAdminUser({
    options: { onSuccess: () => toast.success('User unblocked'), onError: onActionError('Failed to unblock user') }
  })
  const sendPasswordReset = useSendMunicipalityAdminPasswordReset({
    options: {
      onSuccess: () => toast.success('Password reset email sent'),
      onError: onActionError('Failed to send password reset email')
    }
  })

  // Server-side, a user admin can never block themselves or edit their own
  // User Admin flag — the UI mirrors those rules.
  if (!currentUser?.is_user_admin) {
    return (
      <Alert variant="warning">
        You don&apos;t have permission to manage users. Ask a user admin at your
        organization or your ThirdLine contact for access.
      </Alert>
    )
  }

  const handleAdd = () => {
    setEditingUser(null)
    setShowFormModal(true)
  }

  const handleEdit = (user) => {
    setEditingUser(user)
    setShowFormModal(true)
  }

  const handleBlock = (user) => {
    if (window.confirm(`Block ${user.first_name} ${user.last_name}? They will no longer be able to sign in.`)) {
      blockUser.mutate(user.id)
    }
  }

  const handleUnblock = (user) => {
    if (window.confirm(`Unblock ${user.first_name} ${user.last_name}? They will be able to sign in again.`)) {
      unblockUser.mutate(user.id)
    }
  }

  const handlePasswordReset = (user) => {
    if (window.confirm(`Send a password reset email to ${user.email}?`)) {
      sendPasswordReset.mutate(user.id)
    }
  }

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Users</h5>
          <Button variant="primary" size="sm" onClick={handleAdd}>
            Add User
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Reports Role</th>
                  <th>Tags</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {isLoading && <TableLoading columns={COLUMNS} />}
                {isError && <TableError columns={COLUMNS} />}
                {!isLoading && !isError && (users || []).map((user) => {
                  const isSelf = user.id === currentUser.id
                  return (
                    <tr key={user.id}>
                      <td>
                        {user.first_name} {user.last_name}
                        {user.is_user_admin && (
                          <AppBadge variant="primary" className="ms-2">User Admin</AppBadge>
                        )}
                        {isSelf && <span className="text-muted ms-2">(you)</span>}
                      </td>
                      <td>{user.email}</td>
                      <td>
                        {user.has_datum_app
                          ? DATUM_ROLE_LABELS[user.datum_role] || '—'
                          : <span className="text-muted">No access</span>}
                      </td>
                      <td>
                        {(user.datum_tags || []).length === 0
                          ? <span className="text-muted">None</span>
                          : (user.datum_tags || []).map((tag) => (
                            <AppBadge key={tag.id} variant="info" className="me-1">{tag.name}</AppBadge>
                          ))}
                      </td>
                      <td><UserStatusBadge state={statusFor(user)} /></td>
                      <td className="text-end text-nowrap">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => handleEdit(user)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => setReportsUser(user)}
                        >
                          Reports
                        </Button>
                        {user.has_auth0_account && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => handlePasswordReset(user)}
                            disabled={sendPasswordReset.isLoading}
                          >
                            Reset Password
                          </Button>
                        )}
                        {!isSelf && user.has_auth0_account && (
                          user.blocked ? (
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleUnblock(user)}
                              disabled={unblockUser.isLoading}
                            >
                              Unblock
                            </Button>
                          ) : (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleBlock(user)}
                              disabled={blockUser.isLoading}
                            >
                              Block
                            </Button>
                          )
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <DatumTagsCard users={users || []} />

      <UserFormModal
        user={editingUser}
        show={showFormModal}
        onHide={() => setShowFormModal(false)}
      />

      <UserReportsModal
        user={reportsUser}
        show={!!reportsUser}
        onHide={() => setReportsUser(null)}
      />
    </>
  )
}

export default UserSettings
